/**
 * ESM source for Vite/Rollup (named static imports).
 * The CJS sibling (`webRefreshCoordination.cjs`) is auto-generated from this file —
 * edit HERE and run `npm run sync:jtest-cjs` to regenerate.
 * Jest and mobile import the `.cjs` version.
 */

export const REFRESH_LOCK_NAME = 'fcp-web-refresh';
/** ~30s cumulative — covers Vercel cutover + client backoff during deploy. */
export const REFRESH_RETRY_DELAYS_MS = [0, 500, 1500, 3000, 5000, 8000, 13000];

/** HTTP statuses where refresh should retry rather than clear the session. */
export function isRefreshTransientHttpStatus(status) {
  return status === 408 || (status >= 500 && status <= 599);
}

/**
 * Legacy localStorage body refresh is only for cookie-less clients.
 * Never fall back after a transient server failure (deploy cutover).
 */
export function shouldAttemptLegacyBodyRefresh(firstResponseStatus, opts = {}) {
  const { attemptedCookieRefresh = true } = opts;
  if (!attemptedCookieRefresh) return true;
  if (isRefreshTransientHttpStatus(firstResponseStatus)) return false;
  if (firstResponseStatus === 400 || firstResponseStatus === 401) return true;
  return false;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** HTTP statuses where refresh should retry rather than clear the session. */
export function shouldClearSessionOnRefreshFailure(status, code) {
  if (isRefreshTransientHttpStatus(status)) return false;
  if (code === 'REFRESH_RETRY') return false;
  if (code === 'TOKEN_REUSE') return true;
  return true;
}

export function isTransientRefreshFailure(status, code) {
  return isRefreshTransientHttpStatus(status) || code === 'REFRESH_RETRY';
}

export async function readRefreshResponseCode(res) {
  try {
    const body = await res.clone().json();
    return typeof body?.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

/**
 * POST /api/auth/refresh with transient 5xx retry and REFRESH_RETRY follow-ups.
 * @param {() => Promise<Response>} requestRefresh
 * @returns {Promise<{ kind: 'success', token: string, refreshToken?: string, user?: object } | { kind: 'transient' } | { kind: 'auth-failure' }>}
 */
export async function fetchRefreshWithRetry(requestRefresh) {
  let res = null;

  for (let i = 0; i < REFRESH_RETRY_DELAYS_MS.length; i += 1) {
    if (REFRESH_RETRY_DELAYS_MS[i] > 0) {
      await sleep(REFRESH_RETRY_DELAYS_MS[i]);
    }
    res = await requestRefresh();
    if (res.ok) break;

    const code = await readRefreshResponseCode(res);
    if (code === 'REFRESH_RETRY') {
      if (i < REFRESH_RETRY_DELAYS_MS.length - 1) {
        continue;
      }
      break;
    }
    if (!isRefreshTransientHttpStatus(res.status)) break;
  }

  if (!res || !res.ok) {
    const code = res ? await readRefreshResponseCode(res) : null;
    const status = res?.status ?? 0;
    if (isTransientRefreshFailure(status, code)) {
      return { kind: 'transient' };
    }
    return { kind: 'auth-failure', code };
  }

  const data = await res.json();
  if (typeof data.token !== 'string' || !data.token) {
    return { kind: 'auth-failure' };
  }
  return {
    kind: 'success',
    token: data.token,
    refreshToken: typeof data.refreshToken === 'string' ? data.refreshToken : undefined,
    user: data.user,
  };
}

/**
 * @template T
 * @param {() => Promise<T>} fn
 * @returns {Promise<T>}
 */
export async function withWebRefreshLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
}

/**
 * @param {() => Promise<Response>} doRefresh
 */
export async function refreshWithTransientRetry(doRefresh) {
  let lastRes = null;
  for (let i = 0; i < REFRESH_RETRY_DELAYS_MS.length; i += 1) {
    if (REFRESH_RETRY_DELAYS_MS[i] > 0) {
      await sleep(REFRESH_RETRY_DELAYS_MS[i]);
    }
    lastRes = await doRefresh();
    if (lastRes.ok || !isRefreshTransientHttpStatus(lastRes.status)) {
      return lastRes;
    }
  }
  return lastRes;
}
